import { useUIStore } from "@/store/use-ui-store";
import { useLocation, Link } from "react-router-dom";
import { useAuth } from "@/components/auth-provider";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Building,
  Home,
  Users,
  FileText,
  Settings,
  Menu,
  ChevronLeft,
  User,
  LogOut,
  Shield,
  Heart,
  Mail,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";
import { ModeToggle } from "@/components/mode-toggle";
import { NotificationBell } from "@/components/notification-bell";

interface MobileSidebarProps {
  children?: React.ReactNode;
}

export function ResponsiveSidebar({ children }: MobileSidebarProps) {
  return <MobileSidebar>{children}</MobileSidebar>;
}

export function MobileSidebar({ children }: MobileSidebarProps) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;

  const handleSignOut = () => {
    signOut();
  };
  // Centralized UI state (shared across components)
  // Use the zustand store to avoid prop drilling and unnecessary re-renders
  const mobileOpen = useUIStore((s) => s.mobileOpen);
  const setMobileOpen = useUIStore((s) => s.setMobileOpen);

  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const setSidebarCollapsed = useUIStore((s) => s.setSidebarCollapsed);

  const menuItems = [
    {
      title: "Dashboard",
      href: "/landlord/dashboard",
      icon: Home,
      group: "dashboard",
    },
    {
      title: "Properties",
      href: "/landlord/properties",
      icon: Building,
      group: "dashboard",
    },
    {
      title: "Applications",
      href: "/landlord/applications",
      icon: FileText,
      group: "dashboard",
    },
    {
      title: "Inquiries",
      href: "/landlord/inquiries",
      icon: Mail,
      group: "dashboard",
    },
    {
      title: "Tenants",
      href: "/landlord/tenants",
      icon: Users,
      group: "dashboard",
    },
    {
      title: "Leases",
      href: "/landlord/leases",
      icon: FileText,
      group: "dashboard",
    },
    {
      title: "Maintenance",
      href: "/landlord/maintenance",
      icon: Wrench,
      group: "dashboard",
    },
  ];

  const getGroupItems = (groupName: string) =>
    menuItems.filter((item) => item.group === groupName);

  return (
    <div className="flex min-h-screen bg-muted/10">
      {/* Mobile Sheet Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="fixed top-4 left-4 z-40 lg:hidden bg-background border shadow-md"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-64 p-0">
          <div className="flex flex-col h-full bg-background">
            {/* Mobile Header */}
            <div className="p-6 border-b">
              <div className="flex items-center space-x-2">
                <Building className="h-6 w-6 text-primary" />
                <div>
                  <h2 className="font-bold text-lg">HomeSpace Africa</h2>
                  <p className="text-sm text-muted-foreground">
                    Property Management
                  </p>
                </div>
              </div>
            </div>

            {/* Mobile Navigation */}
            <nav className="flex-1 overflow-y-auto px-4 py-6">
              <div className="space-y-6">
                {["dashboard", "analytics", "communication", "other"].map(
                  (groupName) => {
                    const items = getGroupItems(groupName);
                    if (items.length === 0) return null;

                    return (
                      <div key={groupName}>
                        <h3 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide mb-3 px-2">
                          {groupName.charAt(0).toUpperCase() +
                            groupName.slice(1)}
                        </h3>
                        <div className="space-y-1">
                          {items.map((item) => (
                            <Link
                              key={item.href}
                              to={item.href}
                              onClick={() => setMobileOpen(false)}
                              className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                                pathname === item.href
                                  ? "bg-primary text-primary-foreground"
                                  : "text-muted-foreground hover:text-foreground hover:bg-accent"
                              }`}
                            >
                              <item.icon className="h-4 w-4" />
                              {item.title}
                            </Link>
                          ))}
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </nav>

            {/* Mobile Footer */}
            <div className="p-4 border-t">
              <div className="flex items-center space-x-3">
                <Avatar className="h-8 w-8">
                  <AvatarImage
                    src={user?.image || ""}
                    alt={user?.name || "User"}
                  />
                  <AvatarFallback>
                    {user?.name?.charAt(0).toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {user?.name || "User"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.email || ""}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Desktop Collapsible Sidebar - Anchored to Side */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden lg:flex">
        <div
          className={`
            flex flex-col bg-background border-r transition-all duration-300 ease-in-out
            ${sidebarCollapsed ? "w-16" : "w-64"}
          `}
        >
          {/* Desktop Header with Collapse Button */}
          <div
            className={`p-4 border-b ${
              sidebarCollapsed ? "" : "flex items-center justify-between"
            }`}
          >
            {!sidebarCollapsed && (
              <>
                <div className="flex items-center space-x-2">
                  <Building className="h-5 w-5 text-primary" />
                  <span className="font-bold text-sm">HomeSpace</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarCollapsed(true)}
                  className="h-8 w-8"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </>
            )}
            {sidebarCollapsed && (
              <div className="flex justify-center py-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarCollapsed(false)}
                  className="h-8 w-8"
                >
                  <ChevronLeft className="h-4 w-4 rotate-180" />
                </Button>
              </div>
            )}
          </div>

          {/* Desktop Navigation */}
          <nav className="flex-1 overflow-y-auto px-3 py-4">
            <div className="space-y-4">
              {["dashboard", "analytics", "communication", "other"].map(
                (groupName) => {
                  const items = getGroupItems(groupName);
                  if (items.length === 0) return null;

                  return (
                    <div key={groupName}>
                      {!sidebarCollapsed && (
                        <h3 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide mb-3 px-2">
                          {groupName.charAt(0).toUpperCase() +
                            groupName.slice(1)}
                        </h3>
                      )}
                      <div className="space-y-1">
                        {items.map((item) => (
                          <Link
                            key={item.href}
                            to={item.href}
                            title={sidebarCollapsed ? item.title : undefined}
                            className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all duration-300 ${
                              pathname === item.href
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:text-foreground hover:bg-accent"
                            } ${sidebarCollapsed ? "justify-center" : ""}`}
                          >
                            <item.icon className="h-4 w-4 shrink-0" />
                            {!sidebarCollapsed && (
                              <span className="truncate transition-opacity duration-300 delay-100">
                                {item.title}
                              </span>
                            )}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </nav>

          {/* Sidebar Footer */}
          <div className={`${sidebarCollapsed ? "px-2 py-4" : "p-4"} border-t`}>
            <div
              className={`${
                sidebarCollapsed
                  ? "flex flex-col items-center"
                  : "flex items-center space-x-3"
              }`}
            >
              <Avatar className={sidebarCollapsed ? "h-8 w-8" : "h-8 w-8"}>
                <AvatarImage
                  src={user?.image || ""}
                  alt={user?.name || "User"}
                />
                <AvatarFallback>
                  {user?.name?.charAt(0).toUpperCase() || "U"}
                </AvatarFallback>
              </Avatar>
              {!sidebarCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {user?.name || "User"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.email || ""}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div
        className={`
          flex-1 flex flex-col transition-all duration-300 ease-in-out
          ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-64"}
          relative
        `}
      >
        {/* Top Bar */}
        <header className="sticky top-0 z-10 w-full border-b bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60">
          <div className="flex h-14 items-center justify-between px-4">
            <div className="flex items-center">
              <h1 className="text-lg font-semibold">
                {pathname === "/dashboard" && "Dashboard Overview"}
                {pathname === "/landlord/properties" && "Properties Management"}
                {pathname === "/dashboard/tenants" && "Tenants"}
                {pathname === "/landlord/leases" && "Leases"}
                {pathname === "/landlord/maintenance" && "Maintenance Requests"}
                {pathname === "/dashboard/profile" && "Profile Settings"}
                {pathname === "/dashboard/analytics/financial" &&
                  "Financial Analytics"}
                {pathname === "/dashboard/analytics/occupancy" &&
                  "Occupancy Analytics"}
                {pathname === "/dashboard/messages" && "Messages"}
                {pathname === "/dashboard/notifications" && "Notifications"}
                {pathname === "/dashboard/help" && "Help & Support"}
              </h1>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-2">
              <NotificationBell />
              <ModeToggle />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-8 w-8 rounded-full"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src={user?.image || ""}
                        alt={user?.name || "User"}
                      />
                      <AvatarFallback>
                        {user?.name?.charAt(0).toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {user?.name || "User"}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user?.email || ""}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem asChild>
                      <Link to="/landlord/dashboard">
                        <Home className="mr-2 h-4 w-4" />
                        Landlord Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/tenant/dashboard">
                        <Home className="mr-2 h-4 w-4" />
                        Tenant Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/admin/properties">
                        <Shield className="mr-2 h-4 w-4" />
                        Admin Dashboard
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem asChild>
                      <Link to="/profile">
                        <User className="mr-2 h-4 w-4" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/favorites">
                        <Heart className="mr-2 h-4 w-4" />
                        Saved Properties
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/settings">
                        <Settings className="mr-2 h-4 w-4" />
                        Settings
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={handleSignOut}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>
    </div>
  );
}
